<template>
  <GamDialog ref="notificationDialog" :hide-back="true">
    <SimpleContentDialog v-bind="notificationDialogProps()" />
  </GamDialog>
</template>

<script setup lang="ts">
import { SuppressableDialog } from '@/core/data/dialog/dialog.repository';
import { getPushNotificationToken, isPushPermissionGranted } from '@/core/domain/notification/pushNotifications';
import { gambits } from '@/core/gambits';
import { areNotificationsSupported } from '@/core/helpers/ui.helper';
import { NotificationTopics } from '@/views/composables/constants/gam.constants';
import type { SimpleContentDialogType } from '@/views/composables/models/dialog.interface';
import { ref, watch } from 'vue';
import { useToast } from 'vue-toastification';
import GamDialog from '../GamDialog.vue';
import SimpleContentDialog from './SimpleContentDialog.vue';

export interface NotificationPermissionDialogProps {
  isOpen?: boolean;
  message?: string;
}

const props = withDefaults(defineProps<NotificationPermissionDialogProps>(), {
  message: 'dialog.notifications.request',
});

const isSubmitting = ref<boolean>(false);

const notificationDialog = ref<InstanceType<typeof GamDialog>>();
const notificationDialogProps = (): SimpleContentDialogType => {
  return {
    message: props.message,
    rememberChoice: SuppressableDialog.ENABLE_PUSH_NOTIFICATIONS,
    isDisabled: isSubmitting.value,
    action: {
      label: 'dialog.notifications.yes',
      onClick: async () => {
        isSubmitting.value = true;

        const token = await getPushNotificationToken();

        if (token === null || (typeof token === 'string' && token.length === 0)) {
          useToast().error('Did not receive notification push permissions.');

          isSubmitting.value = false;
          notificationDialog.value?.close();

          return;
        }

        await gambits.userService.storeNotificationToken(token, [NotificationTopics.GAMBIT]);

        notificationDialog.value?.close();
        isSubmitting.value = false;
      },
    },
    cancel: {
      label: 'dialog.notifications.no',
      onClick: () => {
        notificationDialog.value?.close();
      },
    },
  };
};

watch(
  () => props.isOpen,
  (isOpen, wasOpen) => {
    if (
      !wasOpen &&
      isOpen &&
      !isPushPermissionGranted() &&
      !gambits.dialogRepository.getDialogSetting(SuppressableDialog.ENABLE_PUSH_NOTIFICATIONS) &&
      areNotificationsSupported.value
    ) {
      if (gambits.dialogRepository.getDialogSetting(SuppressableDialog.NOTIFICATIONS_PROMPT_SKIPPED)) {
        // Show the notification prompt, if we skipped showing it once
        notificationDialog.value?.show();
      } else {
        // Othersiw make note of the skip
        gambits.dialogRepository.suppressDialog(SuppressableDialog.NOTIFICATIONS_PROMPT_SKIPPED);
      }
    }
  },
);
</script>
